import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { de } from 'date-fns/locale';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CTM_Colors, SessionGridRow } from '../../Helper';

interface SessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: SessionGridRow) => void;
  mode?: 'create' | 'edit';
  existingSession?: SessionGridRow;
}

const SessionDialog: React.FC<SessionDialogProps> = ({ open, onClose, onSubmit, mode = 'create', existingSession }) => {
  const [formData, setFormData] = useState<SessionGridRow>({
    id: '',
    date: new Date(),
    name: '',
    info: '',
  });

  // Update form data when dialog opens
  React.useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && existingSession) {
      setFormData(existingSession);
    } else if (mode === 'create') {
      setFormData({ id: '', date: new Date(), name: '', info: '' });
    }
  }, [open, mode, existingSession]);

  const handleInputChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (formData.name !== '' && formData.date) {
      onSubmit(formData);
      onClose();
      // Reset form after submit
      setFormData({ id: '', date: new Date(), name: '', info: '' });
    } else {
      console.log('Name is required!');
      toast.error('Name and date are required!');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: CTM_Colors.ctm_green }}>
        {mode === 'create' ? 'New Testing Session' : 'Edit Testing Session'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          fullWidth
          margin="normal"
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
          <DatePicker
            label="Select Date"
            value={formData.date}
            onChange={(newValue) => handleInputChange('date', newValue)}
            slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
          />
        </LocalizationProvider>
        <TextField
          label="Info"
          value={formData.info}
          onChange={(e) => handleInputChange('info', e.target.value)}
          fullWidth
          multiline
          rows={4}
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary">
          {mode === 'create' ? 'Create' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionDialog;